/**
 * Top shell bar: branding, dev WebSocket status, auth CTA / user menu, theme toggle.
 */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import BrandLogo from '../BrandLogo';
import UserMenu from '../UserMenu';

/**
 * Top application bar for Guardian.
 *
 * @param {object} props
 * @param {boolean} props.showConnectionDebug - Show WS URL chip + retry (dev only).
 * @param {string} props.backendUrl - Current WebSocket URL.
 * @param {boolean} props.showUrlInput - Whether the inline URL editor is visible.
 * @param {boolean} props.isConnected
 * @param {boolean} props.isRetrying
 * @param {string|null} props.connectionError - WS or OAuth error message.
 * @param {string|null} props.authToken
 * @param {object|null} props.guardianUser
 * @param {boolean} props.promoBadgeVisible
 * @param {'light'|'dark'} props.themeMode
 * @param {(url: string) => void} props.onUrlChange
 * @param {(open: boolean) => void} props.onToggleUrlInput
 * @param {() => void} props.onRetryConnection
 * @param {() => void} props.onDismissPromoBadge
 * @param {() => void} props.onOpenAuthModal
 * @param {() => void} props.onSignOut
 * @param {(itemId: string) => void} props.onUserMenuSelect
 * @param {() => void} props.onToggleTheme
 * @param {() => void} [props.onHome] - Navigate to chat home (logo click).
 */
export default function AppHeader({
  showConnectionDebug,
  backendUrl,
  showUrlInput,
  isConnected,
  isRetrying,
  connectionError,
  authToken,
  guardianUser,
  promoBadgeVisible,
  themeMode,
  onUrlChange,
  onToggleUrlInput,
  onRetryConnection,
  onDismissPromoBadge,
  onOpenAuthModal,
  onSignOut,
  onUserMenuSelect,
  onToggleTheme,
  onHome,
}) {
  return (
    <Box
      className="il-shell-header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: onHome ? 'pointer' : 'default',
        }}
        onClick={onHome}
        role={onHome ? 'link' : undefined}
        aria-label={onHome ? 'Go to chat' : undefined}
      >
        <BrandLogo size="md" showText={false} />
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            Interlinked Guardian
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}
          >
            Wildfire intelligence: risk, incidents, reports, and tactics
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showConnectionDebug && (
          <>
            {showUrlInput && (
              <TextField
                size="small"
                value={backendUrl}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="Backend URL (ws://...)"
                sx={{
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    '& fieldset': { borderColor: 'divider' },
                    '&:hover fieldset': { borderColor: 'text.secondary' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: 'text.secondary' },
                }}
                onBlur={() => onToggleUrlInput(false)}
                autoFocus
              />
            )}
            <Chip
              icon={isConnected ? <WifiIcon /> : <WifiOffIcon />}
              label={isConnected ? 'Connected' : isRetrying ? 'Connecting...' : 'Disconnected'}
              color={isConnected ? 'success' : 'error'}
              size="small"
              onClick={() => onToggleUrlInput(!showUrlInput)}
              sx={{ cursor: 'pointer' }}
            />
            {connectionError && (
              <>
                <Chip
                  label={`Error: ${connectionError}`}
                  color="error"
                  size="small"
                  sx={{
                    maxWidth: 200,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                  title={connectionError}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onRetryConnection}
                  disabled={isRetrying}
                  sx={{
                    color: 'error.main',
                    borderColor: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'error.light',
                    },
                  }}
                >
                  {isRetrying ? 'Retrying...' : 'Retry'}
                </Button>
              </>
            )}
          </>
        )}
        {!authToken ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {promoBadgeVisible && (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  maxWidth: 340,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '20px',
                  bgcolor: themeMode === 'dark' ? '#1a3052' : 'secondary.main',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#fff',
                    lineHeight: 1.35,
                    pr: 0.25,
                  }}
                >
                  Sign in to access frontier models and report incidents
                </Typography>
                <IconButton
                  size="small"
                  onClick={onDismissPromoBadge}
                  aria-label="Dismiss"
                  sx={{
                    p: 0.25,
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={onOpenAuthModal}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Sign up / Log in
            </Button>
          </Box>
        ) : (
          <UserMenu
            user={guardianUser}
            authToken={authToken}
            onSignOut={onSignOut}
            onSelectItem={onUserMenuSelect}
          />
        )}
        <IconButton
          onClick={onToggleTheme}
          sx={{ color: 'text.secondary' }}
          title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
        >
          {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}
